import http from './httpClient';

// Toggle for recipe debug logging. Set window.__DEBUG_RECIPES = true in dev console to re-enable temporarily.
const DEBUG_RECIPES = (typeof window !== 'undefined' && window.__DEBUG_RECIPES) || false;

class RecipesService {
  // Get all recipes
  static async getRecipes(businessId = null) {
    try {
  if (DEBUG_RECIPES) console.log('🔍 Fetching all recipes...');
      const bId = Number(
        businessId ?? (typeof window !== 'undefined' ? window.localStorage.getItem('businessId') : null)
      );
      // Complimentary items are included; inactive ones are NOT. is_active = false on a menu
      // item means exactly one thing — it was deleted and had sales history, so the row had to
      // stay behind. Asking for it here put deleted dishes back in Item Mapping.
      //
      // This once carried includeInactive=true "so newly onboarded items appear". That was
      // never the cause: every INSERT INTO MenuItems in the backend sets is_active = true
      // explicitly and the column defaults to true, so a newly created item is always active.
      const data = await http.get(`/recipes?businessId=${bId}&includeComplimentary=true`);
      if (!data || data.success === false) {
        console.error('❌ Failed to fetch recipes:', data);
        throw new Error((data && data.error) || 'Failed to fetch recipes');
      }
      
  if (DEBUG_RECIPES) console.log(`✅ Loaded ${data.data?.length ?? 0} recipes`);
      return data.data;
    } catch (error) {
      console.error('❌ Error fetching recipes:', error);
      throw error;
    }
  }

  // Get all recipes and their ingredients for export
  static async getExportData(businessId = null, startDate = null, endDate = null) {
    try {
      const bId = Number(
        businessId ?? (typeof window !== 'undefined' ? window.localStorage.getItem('businessId') : null)
      );
      let query = `/recipes/export/data?businessId=${bId}`;
      if (startDate && endDate) {
        query += `&startDate=${startDate}&endDate=${endDate}`;
      }
      const data = await http.get(query);
      if (!data || data.success === false) {
        throw new Error((data && data.error) || 'Failed to fetch export recipe data');
      }
      return data.data;
    } catch (error) {
      console.error('❌ Error fetching export recipe data:', error);
      throw error;
    }
  }

  // Get ingredients for a specific recipe
  static async getRecipeIngredients(recipeId, businessId = null) {
    try {
  if (DEBUG_RECIPES) console.log(`🔍 Fetching ingredients for recipe ${recipeId}`);
      const bId = Number(
        businessId ?? (typeof window !== 'undefined' ? window.localStorage.getItem('businessId') : null)
      );
      const data = await http.get(`/recipes/${recipeId}/ingredients?businessId=${bId}`);
      if (!data || data.success === false) {
        console.error('❌ Failed to fetch ingredients:', data);
        throw new Error((data && data.error) || 'Failed to fetch recipe ingredients');
      }
      
  if (DEBUG_RECIPES) console.log(` Loaded ${data.data?.length ?? 0} ingredients for recipe ${recipeId}`);
      return data.data;
    } catch (error) {
      console.error('❌ Error fetching recipe ingredients:', error);
      throw error;
    }
  }

  // Create a new recipe
  static async createRecipe(recipeData, businessId = null) {
    try {
      const rawBusinessId =
        businessId ??
        recipeData?.businessId ??
        (typeof window !== 'undefined' ? window.localStorage.getItem('businessId') : null);
      const bId = Number(rawBusinessId);
      const data = await http.post('/recipes', { ...recipeData, businessId: bId });
  if (!data || data.success === false) throw new Error((data && data.error) || 'Failed to create recipe');
  return data.data;
    } catch (error) {
      console.error('Error creating recipe:', error);
      throw error;
    }
  }

  // Update a recipe
  static async updateRecipe(recipeId, recipeData, businessId = null) {
    try {
      const bId = Number(
        businessId ?? (typeof window !== 'undefined' ? window.localStorage.getItem('businessId') : null)
      );
      const data = await http.put(`/recipes/${recipeId}`, { ...recipeData, businessId: bId });
      if (!data || data.success === false) {
        console.error('❌ Failed to update recipe:', data);
        throw new Error((data && data.error) || 'Failed to update recipe');
      }
      
      return data.data;
    } catch (error) {
      console.error('❌ Error updating recipe:', error);
      throw error;
    }
  }

  // Update recipe ingredients
  static async updateRecipeIngredients(recipeId, ingredients, businessId = null) {
    try {
      const bId = Number(
        businessId ?? (typeof window !== 'undefined' ? window.localStorage.getItem('businessId') : null)
      );
      const data = await http.put(`/recipes/${recipeId}/ingredients`, { ingredients, businessId: bId });
      if (!data || data.success === false) {
        console.error('❌ Server error:', data);
        throw new Error((data && data.error) || 'Failed to update recipe ingredients');
      }
      return data;
    } catch (error) {
      console.error('❌ Error updating recipe ingredients:', error);
      throw error;
    }
  }

  // Read-only pre-flight for the Item Summary: gate + full duplicate scan, nothing written.
  static async validateRecipeIngredients(recipeId, payload) {
    const data = await http.post(`/recipes/${recipeId}/ingredients/validate`, payload);
    if (!data || data.success === false) {
      throw new Error((data && data.error) || 'Failed to validate recipe ingredients');
    }
    return { ok: data.ok, blockers: data.blockers || [], warnings: data.warnings || [], conflicts: data.conflicts || [] };
  }

  // Delete a recipe (soft delete if has sales history)
  static async deleteRecipe(recipeId, businessId = null) {
    try {
      const bId = Number(
        businessId ?? (typeof window !== 'undefined' ? window.localStorage.getItem('businessId') : null)
      );
      const data = await http.delete(`/recipes/${recipeId}?businessId=${bId}`);
      if (!data || data.success === false) {
        console.error('❌ Failed to delete recipe:', data);
        throw new Error((data && data.error) || 'Failed to delete recipe');
      }
      
      
      return data;
    } catch (error) {
      console.error('❌ Error deleting recipe:', error);
      throw error;
    }
  }

  // Update recipe customizations (complimentary_items and add_ons)
  static async updateRecipeCustomizations(recipeId, customizations, businessId = null) {
    try {
      const bId = Number(
        businessId ?? (typeof window !== 'undefined' ? window.localStorage.getItem('businessId') : null)
      );
      const data = await http.put(`/recipes/${recipeId}/customizations`, customizations, {
        headers: { 'x-business-id': bId }
      });
      if (!data || data.success === false) {
        console.error('❌ Failed to update customizations:', data);
        throw new Error((data && data.error) || 'Failed to update customizations');
      }
      return data.data;
    } catch (error) {
      console.error('❌ Error updating customizations:', error);
      throw error;
    }
  }

  // Get a single recipe by ID
  static async getRecipe(recipeId, businessId = null) {
    try {
      if (DEBUG_RECIPES) console.log(`🔍 Fetching recipe ${recipeId}`);
      const bId = Number(
        businessId ?? (typeof window !== 'undefined' ? window.localStorage.getItem('businessId') : null)
      );
      const data = await http.get(`/recipes/${recipeId}?businessId=${bId}`);
      if (!data || data.success === false) {
        console.error('❌ Failed to fetch recipe:', data);
        throw new Error((data && data.error) || 'Failed to fetch recipe');
      }
      if (DEBUG_RECIPES) console.log('✅ Recipe loaded:', data.data);
      return data;
    } catch (error) {
      console.error('❌ Error fetching recipe:', error);
      throw error;
    }
  }

  // Set recipe confirmation status
  static async setRecipeStatus(recipeId, status, businessId = null) {
    const bId = Number(
      businessId ?? (typeof window !== 'undefined' ? window.localStorage.getItem('businessId') : null)
    );
    const data = await http.put(`/recipes/${recipeId}/status`, { status, businessId: bId });
    if (!data.success) throw new Error(data.error || 'Failed to set recipe status');
    return data.data;
  }

  // Check if an ingredient is a custom preparation template
  static async checkPreparation(ingredientName, businessId = null) {
    try {
      const encodedName = encodeURIComponent(ingredientName.trim());
      const bId = Number(
        businessId ?? (typeof window !== 'undefined' ? window.localStorage.getItem('businessId') : null)
      );
      const data = await http.get(`/recipes/check-preparation/${encodedName}?businessId=${bId}`);
      return {
        isPreparation: data.isPreparation || false,
        data: data.data || null
      };
    } catch (error) {
      console.error('❌ Error checking preparation:', error);
      return { isPreparation: false, data: null };
    }
  }
}

export default RecipesService;